import { useState } from 'react';
import {
  apiInvestigados,
  CATEGORIAS_DE_PASSIVO,
  type CategoriaDePassivo,
} from '../lib/api.ts';
import { avisar, avisarErro } from './Avisos.tsx';
import { completarReais, digitarReais } from '../lib/dinheiro.ts';
import { Icone } from './Icone.tsx';
import { BlocoDeLancar, CampoDoPasso } from './PassoAPasso.tsx';
import '../estilos/linha-rapida-2026.css';

/*
 * ⭐ **ESTE ARQUIVO DEIXOU DE SER UMA TELA — 19/09/2026.** O componente
 * `Endividamento` saiu: Restrições e passivo está no desenho de Imóveis
 * (`PassivoDoCaso`, PRD §46.5), que diz as regras da seção — só as partes que o
 * cliente informou, e quantas ficaram de fora. **Ficam aqui as peças que ele
 * usa:** a fila do levantamento, o CENPROT e o lançamento à mão.
 */

/*
 * ⚠️ **Um por vez, em fila — nunca em paralelo.** Um erro de rede no meio de um
 * lote paralelo derrubaria tudo sem dizer qual ficou sem consulta, e o gasto dos
 * que passaram já teria acontecido. Extraída em 19/09/2026 para o tópico no
 * desenho de Imóveis (`PassivoDoCaso`) usar a MESMA fila — nada mudou nela.
 */
export async function levantarEmFila(ids: string[], setBuscando: (id: string | null) => void): Promise<void> {
  let gastou = 0;
  let feitos = 0;
  let poloAtivo = 0;
  let outra = 0;
  let mantidos = 0;
  for (const id of ids) {
    setBuscando(id);
    try {
      const r = await apiInvestigados.levantarPassivo(id);
      gastou += r.custo;
      feitos++;
      poloAtivo += r.noPoloAtivo;
      outra += r.outraPosicao;
      mantidos += r.mantidos ?? 0;
      /* ⚠️ Falha aparece, sempre. Erro engolido deixa a ficha incompleta
         sem pista nenhuma — regra da casa desde 19/08/2026. */
      for (const f of r.falhas) avisarErro(new Error(`${f.rotulo}: ${f.motivo}`));
    } catch (e) {
      avisarErro(e);
    }
  }
  setBuscando(null);
  if (feitos > 0) {
    avisar(
      `${feitos} parte(s) levantada(s). Custo: R$ ${gastou.toFixed(2).replace('.', ',')}.`,
    );
  }
  /* ⛔ Nenhum botão apaga o que o caso já tem (15/09/2026): o que veio diferente fica dito. */
  if (mantidos > 0) {
    avisar(
      `${mantidos === 1 ? '1 lançamento já existia' : `${mantidos} lançamentos já existiam`} com dado diferente ` +
        'na consulta nova e ficou como estava — confira na lista.',
    );
  }
  /*
   * ⛔ **O processo que não é dívida sai daqui DITO — 13/09/2026.** Ele vai a
   * Processos judiciais relevantes, e quem levantou o passivo precisa saber
   * onde procurar; sumir desta lista em silêncio pareceria processo perdido.
   */
  if (poloAtivo + outra > 0) {
    avisar(
      [
        poloAtivo > 0 &&
          `${poloAtivo} ${poloAtivo === 1 ? 'processo' : 'processos'} em que a parte é autora, requerente, ` +
            'exequente ou credora',
        outra > 0 &&
          `${outra} ${outra === 1 ? 'processo' : 'processos'} em que ela não é requerida nem executada, ` +
            'ou cujo papel não se identificou',
      ]
        .filter(Boolean)
        .join(' e ') +
        ' não entraram no endividamento: estão na seção Processos judiciais relevantes, sem somar. ' +
        'Se o papel não se identificou e o processo já estava no endividamento, ele fica nos dois ' +
        'lugares até alguém conferir.',
    );
  }
}


/**
 * Sobe o PDF da pesquisa do CENPROT.
 *
 * ⚠️ **O Hub NÃO guarda o arquivo.** O PDF traz o nome do investigado e dos
 * cartórios; guardar cópia sem necessidade aumentaria a superfície do dado mais
 * sensível da casa. O que fica é o lançamento.
 */
export function SubirCenprot({
  investigadoId,
  aoMudar,
}: {
  investigadoId: string;
  aoMudar: () => Promise<void>;
}) {
  const [subindo, setSubindo] = useState(false);

  async function escolher(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    setSubindo(true);
    try {
      const r = await apiInvestigados.subirProtestoCenprot(investigadoId, arquivo);
      await aoMudar();
      avisar(
        `${r.gravados} lançamento(s) novo(s), ${r.atualizados} completado(s)` +
          (r.consultadoEm ? ` · pesquisa de ${r.consultadoEm.split('-').reverse().join('/')}` : ''),
      );
      if ((r.mantidos ?? 0) > 0) {
        avisar(
          `${r.mantidos === 1 ? '1 protesto já lançado' : `${r.mantidos} protestos já lançados`} com dado diferente no PDF ` +
            `${r.mantidos === 1 ? 'ficou' : 'ficaram'} como estava${r.mantidos === 1 ? '' : 'm'} — confira na lista.`,
        );
      }
      /* ⚠️ A ressalva do valor aparece, sempre: o resumo do CENPROT não traz
         quanto vale cada título, e quem escreve o relatório precisa saber. */
      for (const t of r.ressalvas) avisar(t);
    } catch (err) {
      avisarErro(err);
    } finally {
      setSubindo(false);
    }
  }

  return (
    <p className="ajuda cenprot-envio">
      <label className={`imv-bt p lr-arquivo${subindo ? ' lr-ocupado' : ''}`}>
        <Icone nome="subir" tamanho={13} />
        {subindo ? 'Lendo a pesquisa…' : 'Subir pesquisa do CENPROT (PDF)'}
        <input type="file" accept="application/pdf" onChange={escolher} disabled={subindo} hidden />
      </label>{' '}
      Pesquise em <span className="dado">pesquisaprotesto.com.br</span>, clique em
      Imprimir, salve em PDF e suba aqui. ⚠️ É <strong>pesquisa</strong>, não
      certidão, e cobre só os cartórios participantes.
    </p>
  );
}

/** O lançamento à mão — enquanto não há automação. */
export function FormularioDePassivo({
  investigadoId,
  aoMudar,
  titulo = 'Lançar passivo',
}: {
  investigadoId: string;
  aoMudar: () => Promise<void>;
  /* O tópico no desenho de Imóveis diz de quem é (19/09/2026); o resto é o mesmo. */
  titulo?: string;
}) {
  const [categoria, setCategoria] = useState<CategoriaDePassivo>('protesto');
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [ocorridoEm, setOcorridoEm] = useState('');
  const [referencia, setReferencia] = useState('');

  async function acrescentar(e: React.FormEvent) {
    e.preventDefault();
    if (!descricao.trim()) return;
    try {
      await apiInvestigados.acrescentarPassivo(investigadoId, {
        categoria,
        descricao: descricao.trim(),
        /*
         * ⚠️ **Campo vazio vira `null`, nunca zero.** Zero diria "dívida de
         * R$ 0,00 apurada"; nulo diz "não há valor documentado", que é a
         * verdade quando a fonte não devolve o valor.
         */
        valor: valor.trim() ? Number(valor.replace(/\./g, '').replace(',', '.')) : null,
        ocorridoEm: ocorridoEm || null,
        referencia: referencia.trim() || null,
      });
      setDescricao('');
      setValor('');
      setOcorridoEm('');
      setReferencia('');
      await aoMudar();
    } catch (err) {
      avisarErro(err);
    }
  }

  /* ⭐ No desenho novo desde 18/09/2026 (o bloco de lançar): cada campo diz o que é. */
  return (
    <BlocoDeLancar
      titulo={titulo}
      aoEnviar={(e) => void acrescentar(e)}
      nota={<p>Valor em branco é "não há valor documentado", e não zero.</p>}
      acoes={
        <button type="submit" className="imv-bt primario" disabled={!descricao.trim()}>
          <Icone nome="mais" tamanho={14} /> Lançar
        </button>
      }
    >
      <CampoDoPasso rotulo="Categoria" col={2}>
        <select value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaDePassivo)}>
          {(Object.keys(CATEGORIAS_DE_PASSIVO) as CategoriaDePassivo[]).map((c) => (
            <option key={c} value={c}>
              {CATEGORIAS_DE_PASSIVO[c]}
            </option>
          ))}
        </select>
      </CampoDoPasso>
      <CampoDoPasso rotulo="Onde e o quê" obrigatorio col={4}>
        <input value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="O tabelião e a cidade, a vara e a comarca" />
      </CampoDoPasso>
      <CampoDoPasso rotulo="Valor" col={2} mono>
        <i className="imv-pre">R$</i>
        <input value={valor} onChange={(e) => setValor(digitarReais(e.target.value))} onBlur={(e) => setValor(completarReais(e.target.value))} placeholder="0,00" inputMode="decimal" />
      </CampoDoPasso>
      <CampoDoPasso rotulo="Data do fato" col={2} mono nota="Do protesto, da inscrição ou da distribuição.">
        <input type="date" value={ocorridoEm} onChange={(e) => setOcorridoEm(e.target.value)} />
      </CampoDoPasso>
      <CampoDoPasso rotulo="Referência" col={2} mono>
        <input value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Processo, CDA" />
      </CampoDoPasso>
    </BlocoDeLancar>
  );
}
